import { useQuery } from "@tanstack/react-query";
import { Sparkles, Trophy, Lock, TrendingUp, Users, Award } from "lucide-react";

import { starApi } from "@/lib/extensions-api";
import { formatNumber, formatCurrency, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { StatsCard } from "./components/StatsCard";
import { StarOfferCard } from "./components/StarOfferCard";

export function StarMemberPage() {
  const status = useQuery({ queryKey: ["star-status"], queryFn: starApi.status });
  const offers = useQuery({ queryKey: ["star-offers"], queryFn: starApi.offers });
  const claims = useQuery({ queryKey: ["star-claims"], queryFn: starApi.claims });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Star Membership</h1>
        <p className="text-sm text-surface-500 mt-1">
          Track your Own & Team point activity, qualification progress, and monetary bonuses.
        </p>
      </header>

      {/* Membership status hero */}
      {status.isLoading ? (
        <Skeleton className="h-32 rounded-xl" />
      ) : status.data?.is_star_member ? (
        <Card className="bg-gradient-to-br from-warning-500 via-warning-600 to-warning-700 text-white border-0 overflow-hidden relative">
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
          <CardBody className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur grid place-items-center">
                <Trophy className="h-8 w-8" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-warning-100">Congratulations!</div>
                <div className="text-2xl font-bold">You are a Lifetime Star Member</div>
                <div className="text-sm text-warning-100 mt-1">
                  Activated {formatDateTime(status.data.membership?.activated_at)}
                </div>
              </div>
            </div>
            <Badge tone="neutral" className="!bg-white/20 !text-white !ring-white/20">
              ★ LIFETIME
            </Badge>
          </CardBody>
        </Card>
      ) : (
        <QualificationProgress status={status.data} />
      )}

      {/* Point stats */}
      {status.data && (
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          <StatsCard icon={TrendingUp} tone="info" label="Own Points"
                      value={formatNumber(status.data.points.own)} />
          <StatsCard icon={Users} tone="success" label="Team Points"
                      value={formatNumber(status.data.points.team)}
                      hint="Unlimited generations" />
          <StatsCard icon={Award} tone="warning" label="Combined Points"
                      value={formatNumber(status.data.points.combined)} />
          <StatsCard icon={Sparkles} tone="neutral" label="Total Bonuses"
                      value={
                        claims.data
                          ? formatCurrency(
                              claims.data
                                .filter((c) => c.status === "completed")
                                .reduce((s, c) => s + Number(c.bonus_amount), 0),
                            )
                          : "—"
                      } />
        </div>
      )}

      {/* Active offers */}
      {status.data?.is_star_member && (
        <section>
          <h2 className="text-lg font-semibold text-surface-900 mb-3">Active Offers</h2>
          {offers.isLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-64 rounded-xl" />
              ))}
            </div>
          ) : (offers.data ?? []).length === 0 ? (
            <EmptyState icon={Sparkles} title="No active offers"
                          description="Check back soon — new offers are added regularly." />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {offers.data!.map((o) => <StarOfferCard key={o.id} offer={o} />)}
            </div>
          )}
        </section>
      )}

      {/* Claims history */}
      <section>
        <Card>
          <CardHeader><CardTitle>Bonus History</CardTitle></CardHeader>
          <CardBody className="p-0">
            {claims.isLoading ? (
              <div className="p-5 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : (claims.data ?? []).length === 0 ? (
              <EmptyState icon={Award} title="No bonuses yet"
                            description="Qualify for a Star Offer to earn your first bonus." />
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Offer</TH>
                    <TH className="text-right">Own</TH>
                    <TH className="text-right">Team</TH>
                    <TH className="text-right">Bonus</TH>
                    <TH>Status</TH>
                    <TH>Received</TH>
                  </TR>
                </THead>
                <tbody>
                  {claims.data!.map((c) => (
                    <TR key={c.id}>
                      <TD className="font-medium">{c.offer_name}</TD>
                      <TD className="text-right">{formatNumber(c.own_points)}</TD>
                      <TD className="text-right">{formatNumber(c.team_points)}</TD>
                      <TD className="text-right font-semibold text-success-700">
                        {formatCurrency(c.bonus_amount)}
                      </TD>
                      <TD><StatusPill status={c.status} /></TD>
                      <TD className="text-xs text-surface-500">
                        {formatDateTime(c.completed_at ?? c.created_at)}
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </section>
    </div>
  );
}

function QualificationProgress({ status }: { status: any }) {
  if (!status) return null;
  const rule = status.qualification_rule;
  if (!rule) return null;

  const ownPct = Math.min(100, (status.points.own / rule.min_own_points) * 100);
  const teamPct = Math.min(100, (status.points.team / rule.min_team_points) * 100);
  const ownOnlyPct = Math.min(100, (status.points.own / rule.own_only_threshold) * 100);

  return (
    <Card className="border-2 border-warning-200 bg-warning-50/40">
      <CardBody>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-warning-100 grid place-items-center shrink-0">
            <Lock className="h-7 w-7 text-warning-600" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase font-semibold text-warning-700">Not a Star Member yet</div>
            <div className="text-lg font-bold text-surface-900 mt-1">
              Two paths to lifetime membership
            </div>
            <p className="text-sm text-surface-600 mt-1">
              Reach any one of the paths below to unlock Star Membership forever.
            </p>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="p-4 rounded-lg bg-white border border-warning-200">
                <div className="text-2xs font-bold uppercase tracking-wide text-warning-700">Path A</div>
                <div className="text-sm font-semibold text-surface-900 mt-1">Own + Team</div>
                <div className="mt-3 space-y-2">
                  <MiniBar label="Own"  cur={status.points.own}  tgt={rule.min_own_points}  pct={ownPct}  />
                  <MiniBar label="Team" cur={status.points.team} tgt={rule.min_team_points} pct={teamPct} />
                </div>
              </div>
              <div className="p-4 rounded-lg bg-white border border-warning-200">
                <div className="text-2xs font-bold uppercase tracking-wide text-warning-700">Path B</div>
                <div className="text-sm font-semibold text-surface-900 mt-1">Own Only</div>
                <div className="mt-3">
                  <MiniBar label="Own" cur={status.points.own} tgt={rule.own_only_threshold} pct={ownOnlyPct} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function MiniBar({ label, cur, tgt, pct }: { label: string; cur: number; tgt: number; pct: number }) {
  return (
    <div>
      <div className="flex justify-between text-xs">
        <span className="text-surface-600">{label}</span>
        <span className="font-mono text-surface-800">{formatNumber(cur)} / {formatNumber(tgt)}</span>
      </div>
      <div className="mt-1 h-1.5 bg-surface-100 rounded-full overflow-hidden">
        <div className="h-full bg-warning-500 transition-all"
              style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
